import {
  CheckCircle2,
  DollarSign,
  Eye,
  Filter,
  MoreVertical,
  Search,
  XCircle,
} from 'lucide-react'

import { useState } from 'react'

import { useToast } from '@/hooks/useToast'
import { useBusiness } from '@/contexts/BusinessContext'

import { Badge, type BadgeTone } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

import { PageHeader, StatsCards } from '@/components/atoms'
import { ReturnDetailsDialog } from '@/components/returns/ReturnDetailsDialog'

import { ReturnRequest, ReturnStats } from '@/types/returns'

const RETURN_STATUS_TONE: Partial<Record<ReturnRequest['status'], BadgeTone>> = {
  pending: 'warning',
  approved: 'info',
  completed: 'success',
  rejected: 'error',
}

export const ReturnsDashboard = () => {
  const { toast } = useToast()
  const { formatCurrency } = useBusiness()
  const [searchTerm, setSearchTerm] = useState('')
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [selectedRequest, setSelectedRequest] = useState<ReturnRequest | null>(null)

  // Mock Data
  const stats: ReturnStats = {
    pendingRequests: 5,
    approvedToday: 2,
    totalRefunded: 1250.0,
    averageProcessingTime: 24,
  }

  const [requests, setRequests] = useState<ReturnRequest[]>([
    {
      id: 'RET-1001',
      orderId: 'ORD-5023',
      customerName: 'John Doe',
      customerEmail: 'john@example.com',
      items: [
        {
          productId: 'p1',
          productName: 'Wireless Headphones',
          quantity: 1,
          price: 199.99,
          reason: 'Defective',
          condition: 'Opened',
        },
      ],
      status: 'pending',
      refundAmount: 199.99,
      refundMethod: 'original',
      createdAt: new Date('2024-06-10'),
      updatedAt: new Date('2024-06-10'),
    },
    {
      id: 'RET-1002',
      orderId: 'ORD-4988',
      customerName: 'Jane Smith',
      customerEmail: 'jane@example.com',
      items: [
        {
          productId: 'p2',
          productName: 'Cotton T-Shirt',
          quantity: 2,
          price: 25.0,
          reason: 'Size too small',
          condition: 'New',
        },
      ],
      status: 'approved',
      refundAmount: 50.0,
      refundMethod: 'store_credit',
      trackingNumber: 'TRK-998877',
      createdAt: new Date('2024-06-08'),
      updatedAt: new Date('2024-06-09'),
    },
  ])

  const handleStatusChange = (id: string, newStatus: ReturnRequest['status']) => {
    setRequests(requests.map((r) => (r.id === id ? { ...r, status: newStatus } : r)))

    // Update selected request if it's open in dialog
    if (selectedRequest && selectedRequest.id === id) {
      setSelectedRequest({ ...selectedRequest, status: newStatus })
    }

    toast({
      title: 'Status updated',
      description: `Return request ${id} marked as ${newStatus}`,
    })
  }

  const handleViewDetails = (request: ReturnRequest) => {
    setSelectedRequest(request)
    setDetailsOpen(true)
  }

  const handleApprove = (id: string) => handleStatusChange(id, 'approved')
  const handleReject = (id: string) => handleStatusChange(id, 'rejected')

  return (
    <div className="space-y-4">
      <PageHeader title="Returns" description="Manage return requests and process refunds" />

      <StatsCards
        stats={[
          { title: 'Pending requests', value: stats.pendingRequests },
          { title: 'Approved today', value: stats.approvedToday },
          { title: 'Refunded (30 days)', value: formatCurrency(stats.totalRefunded) },
          { title: 'Avg. processing time', value: `${stats.averageProcessingTime}h` },
        ]}
      />

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <div className="relative w-full sm:w-64">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by order or customer"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
              />
            </div>
            <Button variant="outline" size="icon" aria-label="Filter returns">
              <Filter className="size-4" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          <div className="overflow-x-auto border-t border-border-subtle">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Return</TableHead>
                  <TableHead>Order</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {requests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell className="font-semibold text-ink">{request.id}</TableCell>
                    <TableCell className="cursor-pointer text-ink underline-offset-4 hover:underline">
                      {request.orderId}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-0.5">
                        <span className="font-medium text-ink">{request.customerName}</span>
                        <span className="text-xs text-muted-foreground">{request.customerEmail}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        {request.items.map((item, idx) => (
                          <span key={idx} className="text-sm">
                            {item.quantity} × {item.productName}
                          </span>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCurrency(request.refundAmount)}
                    </TableCell>
                    <TableCell>
                      <Badge tone={RETURN_STATUS_TONE[request.status] ?? 'soft'} className="capitalize">
                        {request.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="tabular-nums text-muted-foreground">
                      {request.createdAt.toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" aria-label={`Actions for ${request.id}`}>
                            <MoreVertical className="size-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleViewDetails(request)}>
                            <Eye className="mr-2 size-4" />
                            View details
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {request.status === 'pending' && (
                            <>
                              <DropdownMenuItem onClick={() => handleApprove(request.id)}>
                                <CheckCircle2 className="mr-2 size-4" />
                                Approve return
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleReject(request.id)}>
                                <XCircle className="mr-2 size-4" />
                                Reject return
                              </DropdownMenuItem>
                            </>
                          )}
                          {request.status === 'approved' && (
                            <DropdownMenuItem
                              onClick={() => handleStatusChange(request.id, 'completed')}
                            >
                              <DollarSign className="mr-2 size-4" />
                              Process refund
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <ReturnDetailsDialog
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
        request={selectedRequest}
        onApprove={handleApprove}
        onReject={handleReject}
      />
    </div>
  )
}
